/* eslint-disable camelcase */
import React, { useState } from 'react';
import useStyles from 'isomorphic-style-loader/useStyles';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';

// app: components
import AlacarteBookDealOverlay from 'components/AlacarteBookDealOverlay';
import AlacarteStatusIndicator from 'components/AlacarteStatusIndicator';
import BookRankOverlay from 'components/BookRankOverlay';
import Link from 'components/Link';

// app: services
import assets from 'services/assets';
import formatter from 'services/formatter';
import route from 'services/route';

import CssBookShelfTile from './BookShelfTile.scss';

function BookShelfTile(props) {
  const {
    alacarte,
    alacarte_available,
    authors,
    backText,
    className,
    height,
    image_aspect_ratio,
    image_name,
    isbn,
    lazy,
    onClick,
    ranking_recommendations,
    price,
    signature_,
    style,
    title,
    width,
  } = props;
  useStyles(CssBookShelfTile);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageErrored, setImageErrored] = useState(false);
  const displayType = useSelector(state => state.dimensions.displayType);
  const owned = useSelector(state => state.user.owned_books?.includes(isbn));

  const imageSrc = image_name
    ? assets.coverUrl(image_name, {
        resize: 'fit-in',
        width,
        height,
      })
    : null;
  const imageSrcSet = image_name
    ? assets.srcSet(
        image_name,
        [
          { w: width, h: height, resize: 'fit-in' },
          { w: width * 2, h: height * 2, resize: 'fit-in' },
        ],
        assets.coverUrl,
      )
    : null;

  const imageSrcProps = {};
  if (!lazy && imageSrc) {
    imageSrcProps.src = imageSrc;
    imageSrcProps.srcSet = imageSrcSet;
  }

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageErrored(true);
  };

  const loaded = image_name && (!lazy || imageLoaded);
  const authorInfo = authors ? formatter.authorsList.toString(authors) : null;
  const altText = title ? `${title} by ${authorInfo}` : null;
  const linkUrl = isbn ? route.book(isbn, signature_) : null;

  const padding = displayType === 'mobile' ? 5 : 0;
  const rankOverlayHeight = displayType === 'mobile' ? 33 : 60;
  const isLandscape = image_aspect_ratio > 1;

  const gutter = 5;
  const imageWidth = width - gutter * 2;

  let imageTopCorner;
  if (isLandscape) {
    imageTopCorner = {
      bottom: imageWidth / image_aspect_ratio - padding,
      right: gutter + padding,
    };
  } else {
    imageTopCorner = {
      bottom: height - padding,
      right: (width - (height * image_aspect_ratio + gutter)) / 2 + padding,
    };
  }

  const iconRadius = AlacarteStatusIndicator.ICON_WIDTH / 2;
  const iconRight = imageTopCorner.right - iconRadius;
  const iconBottom = imageTopCorner.bottom - iconRadius;

  return (
    <div
      style={{
        ...style,
        minWidth: width,
        width,
        minHeight: height + iconRadius,
      }}
      className={classNames('book-shelf-tile', className, {
        'book-shelf-tile--loaded': loaded,
        'book-shelf-tile--errored': imageErrored,
      })}
    >
      {loaded && alacarte ? (
        <AlacarteStatusIndicator
          available={alacarte_available}
          isbn={isbn}
          signature_={signature_}
          style={{ position: 'absolute', right: iconRight, bottom: iconBottom }}
        />
      ) : null}
      {loaded && ranking_recommendations ? (
        <BookRankOverlay
          ranking={ranking_recommendations}
          size={displayType === 'mobile' ? 'small' : 'medium'}
          style={{
            position: 'absolute',
            top: 'unset',
            right: displayType === 'mobile' ? imageTopCorner.right + 11 : imageTopCorner.right + 2,
            bottom: imageTopCorner.bottom - rankOverlayHeight,
          }}
        />
      ) : null}

      {imageErrored ? (
        <Link to={linkUrl} title={altText} onClick={onClick} referrer={backText}>
          <div style={{ height, width: width * 0.8 }} className="book-shelf-tile__missing">
            <p>
              <strong>{title}</strong>
            </p>
            <p>By {authorInfo}</p>
          </div>
        </Link>
      ) : (
        <React.Fragment>
          {image_name ? (
            <Link to={linkUrl} onClick={onClick} title={altText} referrer={backText}>
              <img
                {...imageSrcProps}
                alt=""
                height={height}
                width={width}
                onLoad={lazy ? handleImageLoad : null}
                onError={handleImageError}
                data-src={imageSrc}
                data-src-set={imageSrcSet}
              />
              {!owned && loaded && price?.discount_text ? (
                <AlacarteBookDealOverlay
                  price={price}
                  style={{ bottom: 5, right: imageTopCorner.right + 5 }}
                />
              ) : null}
            </Link>
          ) : null}

          {loaded ? null : <Skeleton height={height} width={width * 0.8} />}
        </React.Fragment>
      )}
    </div>
  );
}

BookShelfTile.propTypes = {
  alacarte: PropTypes.bool,
  alacarte_available: PropTypes.bool,
  authors: PropTypes.arrayOf(
    PropTypes.shape({ first_name: PropTypes.string, last_name: PropTypes.string }),
  ),
  backText: PropTypes.string,
  className: PropTypes.string,
  height: PropTypes.number,
  image_aspect_ratio: PropTypes.number,
  image_name: PropTypes.string,
  isbn: PropTypes.string,
  lazy: PropTypes.bool,
  onClick: PropTypes.func,
  price: PropTypes.shape({
    discount_text: PropTypes.string,
  }),
  ranking_recommendations: PropTypes.number,
  signature_: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  style: PropTypes.object,
  title: PropTypes.string,
  width: PropTypes.number,
};

BookShelfTile.defaultProps = {
  alacarte: false,
  alacarte_available: false,
  authors: [],
  backText: null,
  className: null,
  height: 250,
  image_aspect_ratio: null,
  image_name: null,
  isbn: null,
  lazy: false,
  onClick: null,
  ranking_recommendations: null,
  price: null,
  signature_: null,
  style: {},
  title: null,
  width: 250,
};

export default React.memo(BookShelfTile);
